import { Link, useParams } from 'react-router-dom';
import { getAllPosts, getAllCategories, getPostsByCategory, getCategoryById } from '../data/blog';
import BlogHeader from '../components/BlogHeader';
import BlogFooter from '../components/BlogFooter';
import Breadcrumbs from '../components/Breadcrumbs';

export default function BlogListPage() {
  const { categoryId } = useParams<{ categoryId?: string }>();
  const allCategories = getAllCategories();
  const posts = categoryId ? getPostsByCategory(categoryId) : getAllPosts();
  const activeCategory = categoryId ? getCategoryById(categoryId) : undefined;

  const title = activeCategory ? activeCategory.label : 'Blog';
  const subtitle = activeCategory
    ? activeCategory.description
    : 'Advice, insights, and guides for couples.';

  return (
    <div className="min-h-screen flex flex-col text-white">
      <BlogHeader />

      <main className="pt-24 pb-20 px-6 max-w-2xl mx-auto w-full">
        {categoryId && activeCategory && (
          <Breadcrumbs
            items={[
              { label: 'Home', href: '/' },
              { label: 'Blog', href: '/blog' },
              { label: activeCategory.label },
            ]}
          />
        )}
        <header className="mb-10">
          <h1 className="text-4xl font-extrabold mb-3">{title}</h1>
          <p className="text-slate-400 text-lg">{subtitle}</p>
        </header>

        {/* Category filter */}
        {allCategories.length > 0 && (
          <nav className="flex flex-wrap gap-2 mb-10">
            <Link
              to="/blog"
              className={`px-4 py-1.5 rounded-full text-sm font-semibold border transition-colors ${
                !categoryId
                  ? 'bg-primary/15 border-primary/30 text-primary'
                  : 'border-white/10 text-slate-400 hover:text-white hover:border-white/20'
              }`}
            >
              All
            </Link>
            {allCategories.map((cat) => (
              <Link
                key={cat.id}
                to={`/blog/category/${cat.id}`}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold border transition-colors ${
                  categoryId === cat.id
                    ? 'bg-primary/15 border-primary/30 text-primary'
                    : 'border-white/10 text-slate-400 hover:text-white hover:border-white/20'
                }`}
              >
                {cat.label}
              </Link>
            ))}
          </nav>
        )}

        {/* Post list */}
        {posts.length === 0 ? (
          <p className="text-slate-400">No posts yet.</p>
        ) : (
          <ul className="space-y-4">
            {posts.map((post) => {
              const formattedDate = new Date(post.date).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              });
              return (
                <li key={post.slug}>
                  <Link
                    to={`/blog/${post.slug}`}
                    className="block bg-white/5 hover:bg-white/10 transition-colors rounded-2xl p-6 group"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <Link
                        to={`/blog/category/${post.category}`}
                        onClick={(e) => e.stopPropagation()}
                        className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold border border-primary/20 capitalize hover:bg-primary/20 transition-colors"
                      >
                        {post.category.replace(/-/g, ' ')}
                      </Link>
                      <span className="text-slate-500 text-xs">{post.readingTime} min read</span>
                    </div>
                    <h2 className="text-xl font-bold mb-2 group-hover:text-primary transition-colors">
                      {post.title}
                    </h2>
                    <p className="text-slate-400 text-sm leading-relaxed mb-4">
                      {post.description}
                    </p>
                    <time className="text-slate-500 text-xs" dateTime={post.date}>
                      {formattedDate}
                    </time>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </main>

      <BlogFooter />
    </div>
  );
}
